import React, { useEffect, useRef } from 'react';

/**
 * Toast overlay that integrates with React Aria's overlay system.
 *
 * Sets the `data-react-aria-top-layer` attribute to mark this element as
 * part of React Aria's overlay stack. This prevents React Aria from marking
 * the toast as `inert` when modals are open, ensuring toasts remain visible
 * and interactive.
 *
 * Creates a full-screen fixed overlay with high z-index for proper stacking
 * above modals. Uses `pointer-events: none` to allow clicks to pass through
 * the overlay to page content. Child toast elements use `pointer-events: auto`
 * to capture interactions.
 */
export const ToastOverlay: React.FC<Props> = ({ children }) => {
	const overlayRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const element = overlayRef.current;
		if (!element) return;

		// Mark as React Aria overlay so it's treated as part of the overlay system
		element.setAttribute('data-react-aria-top-layer', 'true');
	}, []);

	return (
		<div ref={overlayRef} className="pointer-events-none fixed inset-0 z-[9999]">
			{children}
		</div>
	);
};

type Props = Readonly<{
	children: React.ReactNode;
}>;
